/**
 * India GST validation helpers · Feb 2026
 *
 * Shared by CustomerFormDialog + SupplierFormDialog so the two masters
 * apply IDENTICAL rules — 15-char structural check, state-code auto-
 * derive, PAN auto-extract. The old codebase duplicated these constants
 * inside CustomerFormDialog.jsx; consolidating here removes drift risk
 * (adding Ladakh once vs. twice).
 */

// GST state-code → state-name map (official codes 01-38 from GSTN).
export const GST_STATE_CODES = {
  "01": "JAMMU & KASHMIR", "02": "HIMACHAL PRADESH", "03": "PUNJAB",
  "04": "CHANDIGARH", "05": "UTTARAKHAND", "06": "HARYANA",
  "07": "DELHI", "08": "RAJASTHAN", "09": "UTTAR PRADESH",
  "10": "BIHAR", "11": "SIKKIM", "12": "ARUNACHAL PRADESH",
  "13": "NAGALAND", "14": "MANIPUR", "15": "MIZORAM",
  "16": "TRIPURA", "17": "MEGHALAYA", "18": "ASSAM",
  "19": "WEST BENGAL", "20": "JHARKHAND", "21": "ODISHA",
  "22": "CHHATTISGARH", "23": "MADHYA PRADESH", "24": "GUJARAT",
  "25": "DAMAN & DIU", "26": "DADRA & NAGAR HAVELI",
  "27": "MAHARASHTRA", "28": "ANDHRA PRADESH (OLD)",
  "29": "KARNATAKA", "30": "GOA", "31": "LAKSHADWEEP",
  "32": "KERALA", "33": "TAMIL NADU", "34": "PUDUCHERRY",
  "35": "ANDAMAN & NICOBAR", "36": "TELANGANA",
  "37": "ANDHRA PRADESH", "38": "LADAKH",
};

// GSTIN structural regex.
//   Slots 1-2   → state code (digits)
//   Slots 3-7   → PAN prefix (five letters)
//   Slots 8-11  → PAN sequence (four digits)
//   Slot  12    → PAN check letter
//   Slot  13    → entity number (1-9, A-Z)
//   Slot  14    → literal "Z"
//   Slot  15    → check digit (0-9, A-Z)
export const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

export const isValidGstin = (g) => GSTIN_RE.test((g || "").trim().toUpperCase());

/**
 * Given a raw GSTIN input, derive `{ state_code, state, pan }` when the
 * structure is valid. Empty fields are only filled — never overwritten —
 * so operator-entered values are preserved.
 */
export function deriveGstFields(raw, current = {}) {
  const g = (raw || "").toUpperCase().replace(/\s+/g, "");
  const patch = { gstin: g };
  if (!isValidGstin(g)) return patch;
  const sc = g.slice(0, 2);
  if (!(current.state_code || "").trim()) patch.state_code = sc;
  if (!(current.state || "").trim() && GST_STATE_CODES[sc]) patch.state = GST_STATE_CODES[sc];
  if (!(current.pan || "").trim()) patch.pan = g.slice(2, 12);
  return patch;
}
